/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import {
  Avatar,
  Button,
  Callout,
  Columns,
  H1,
  H2,
  IconPencil,
  Link,
  ProgressBar,
  ScreenReaderText,
  Text,
  WalletList,
} from "atoms";
import iconCheckRounded from "atoms/icons/check_rounded.svg";
import { constants, featureFlags } from "config";
import { useAuth, User } from "features/auth";
import {
  useAdminUserRefresh,
  useAdminUserSocketRefresh,
  useAdminUserUpdate,
} from "features/admin";
import { HUGS } from "../data/hugpasses";
import { DiscordConnect } from "./DiscordConnect";
import { LinkedTokenIds } from "./LinkedTokenIds";
import { ProfileDiscord } from "./ProfileDiscord";
import { RoleOverrideButton } from "./RoleOverrideButton";
import { ProfileReviews } from "../../reviews/components/ProfileReviews";
import { useProfilePoints } from "../api/useProfile";
import { RewardLedgerResponse } from "../types";
import { ProfileAdminRewards } from "./ProfileAdminRewards";
import { ProfileRewards } from "./ProfileRewards";

const ROLES = ["Hugger", "OG Hugger", "hedgeHUG and groupHUG"];

const SUPER_ROLES = [
  "Hugger",
  "OG Hugger",
  "Super Pug Posse",
  "hedgeHUG Appreciation Society",
  "Hugs-Only Hall of Justice",
  "Bear Hugging Brigade",
  "Stealth Mode Squad",
  "Snuggly Buggly Goodies Club",
  "Moonbeam Media Team",
  "AiR Boosters",
  "Touching Grass Group",
  "Elevate",
  "hedgeHUG and groupHUG",
  "Curator",
  "Community Curator",
];

type ProfileParams = {
  user: User;
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
  rewards?: RewardLedgerResponse;
};

const safeStringArray = (s: string[] | undefined) =>
  (s && s.length > 0 ? s : ["None"]).join(", ");

function Profile({
  user,
  rewards,
  isAdmin = false,
  isSuperAdmin = false,
}: ProfileParams) {
  const preferredHug = HUGS[user.hugImageIds?.[0] ?? 0];
  let avatarSrc: string;
  if (user.avatarUrl) {
    avatarSrc = user.avatarUrl;
  } else {
    avatarSrc = `https://thehug.xyz/assets/tokens/${preferredHug.image}`;
  }

  const { refreshAuth } = useAuth();
  const { data: profilePoints } = useProfilePoints(user);

  const {
    adminUserAddDiscordOverride,
    adminUserRemoveDiscordOverride,
    adminUserAddPlatformRole,
    adminUserRemovePlatformRole,
    adminUserResetDiscord,
    isLoading,
  } = useAdminUserUpdate();

  useAdminUserSocketRefresh();
  const adminRefreshUser = useAdminUserRefresh();

  const showRewards = isAdmin && rewards && rewards?.data?.length;

  const hasAdminRole = user?.roles.find((o) => o === "admin");
  const removeAdminRole = () => adminUserRemovePlatformRole(user, "admin");
  const addAdminRole = () => adminUserAddPlatformRole(user, "admin");

  const hasSuperAdminRole = user?.roles.find((o) => o === "superAdmin");
  const removeSuperAdminRole = () =>
    adminUserRemovePlatformRole(user, "superAdmin");
  const addSuperAdminRole = () => adminUserAddPlatformRole(user, "superAdmin");

  const resetDiscord = () => adminUserResetDiscord(user);

  const isQA = user?.roles?.find((u) => u === "qa");
  const removeQA = () => adminUserRemovePlatformRole(user, "qa");
  const addQA = () => adminUserAddPlatformRole(user, "qa");

  const isCuratorLevel1 = user?.roles.find((o) => o === "curator-level-1");
  const isCuratorLevel2 = user?.roles.find((o) => o === "curator-level-2");
  const isCuratorLevel3 = user?.roles.find((o) => o === "curator-level-3");
  const isHolder =
    !!user?.utilities?.find((u) => u === "Hugger") ||
    !!user?.guestPasses?.length;

  const removeCuratorLevel2 = () =>
    adminUserRemovePlatformRole(user, "curator-level-2");
  const addCuratorLevel2 = () =>
    adminUserAddPlatformRole(user, "curator-level-2");

  const featuredRole = () => {
    if (isCuratorLevel3) {
      return "Curator (Level 3)";
    }
    if (isCuratorLevel2) {
      return "Jr. Curator (Level 2)";
    }
    if (isCuratorLevel1) {
      return "Hugger (Level 1)";
    }
    if (isHolder) {
      return "User (HUG Holder)";
    }
    return "User (Not Holding)";
  };

  const profileNextRole = () => {
    const lifetimeEarnings = profilePoints?.lifetimeEarnings || 0;
    if (
      (isCuratorLevel3 || isCuratorLevel2) &&
      lifetimeEarnings >= constants.pointsRequiredLevel3
    ) {
      return (
        <Text>
          <strong>Congratulations!</strong>
          <br />
          <Text size="xs" as="span">
            You have met the requirements to become
            a&nbsp;Curator&nbsp;(Level&nbsp;3). <br />
            You will gain new abilities as we release&nbsp;new&nbsp;features.
          </Text>
        </Text>
      );
    }
    if (isCuratorLevel2) {
      return (
        <Text>
          Keep reviewing projects and moderating reviews and
          <br />
          earn{" "}
          <strong>
            {constants.pointsRequiredLevel3 - lifetimeEarnings} HUG&nbsp;Points
          </strong>{" "}
          to become a <strong>Curator (Level&nbsp;3)</strong>
        </Text>
      );
    }
    if (isCuratorLevel1) {
      if (lifetimeEarnings >= constants.pointsRequiredLevel2) {
        return (
          <Text>
            Congratulations! You can become a{" "}
            <strong>Jr. Curator (Level 2)</strong>
            <br />
            and moderate reviews from other Huggers. <br />
            <Link to="/become-a-moderator">Show Me How.</Link>
          </Text>
        );
      }
      return (
        <Text>
          Keep reviewing projects and earn{" "}
          <strong>
            {constants.pointsRequiredLevel2 - lifetimeEarnings} HUG Points
          </strong>
          <br />
          to become a Jr. Curator (Level 2).
        </Text>
      );
    }
    return (
      <Text>
        Become a <strong>HUGGER</strong> and earn <strong>HUG Points</strong>{" "}
        for reviewing projects.
        <br />
        <Link to="/become-a-reviewer">Show Me How.</Link>
      </Text>
    );
  };
  const pointsLifetime = profilePoints?.lifetimeEarnings.toLocaleString();
  const pointsBalance = profilePoints?.pointsBalance.toLocaleString();
  const pointsStaked = profilePoints?.stakedBalance.toLocaleString();

  return (
    <main>
      <ProfileColumns>
        <ProfileSide>
          <ProfileHeader>
            <AvatarWrapper>
              <SAvatar alt={preferredHug.description} src={avatarSrc} />
            </AvatarWrapper>
            <div>
              <Name size="md">
                {user.name ? user.name : "Anonymous Hugger"}
              </Name>
              <Text size="xs">{featuredRole()}</Text>

              {(!user.name || !user.email) && (
                <FinishProfile>
                  <Text size="xxs" bold>
                    Finish setting up your profile
                  </Text>

                  <FinishProfileList>
                    <Text as="li" size="xxs">
                      <DisplayCheckbox checked /> Connect your wallet
                    </Text>
                    <Text as="li" size="xxs">
                      <DisplayCheckbox checked={!!user.name} />
                      Create username
                    </Text>
                    <Text as="li" size="xxs">
                      <DisplayCheckbox checked={!!user.email} />
                      Add email address
                    </Text>
                  </FinishProfileList>
                </FinishProfile>
              )}

              <EditProfile to="edit" title="edit profile">
                <IconPencil aria-hidden="true" />
                Edit profile
              </EditProfile>
            </div>
          </ProfileHeader>
        </ProfileSide>
        <ProfileMain css={{ display: "flex", flexWrap: "wrap" }}>
          {profilePoints && (
            <>
              <ProfileProgress>
                {profileNextRole()}
                <Progress>
                  <ProgressBar
                    value={profilePoints.lifetimeEarnings}
                    max={3000}
                  >
                    <span>
                      <ScreenReaderText>
                        {pointsLifetime} points
                      </ScreenReaderText>
                    </span>
                  </ProgressBar>
                  <ProgressMilestones>
                    <Text
                      as="li"
                      size="xxs"
                      css={{
                        left: `calc((${constants.pointsEarnedBecomeReviewer} / 3000) * 100%)`,
                      }}
                    >
                      <strong>Hugger</strong>{" "}
                      {constants.pointsEarnedBecomeReviewer}
                      <ScreenReaderText>points</ScreenReaderText>
                    </Text>
                    <Text
                      as="li"
                      size="xxs"
                      css={{
                        left: `calc((${constants.pointsRequiredLevel2} / 3000) * 100%)`,
                      }}
                    >
                      <strong>Jr. Curator</strong>{" "}
                      {constants.pointsRequiredLevel2.toLocaleString()}
                      <ScreenReaderText>points</ScreenReaderText>
                    </Text>
                    <Text
                      as="li"
                      size="xxs"
                      css={{
                        left: `calc((${constants.pointsRequiredLevel3} / 3000) * 100%)`,
                      }}
                    >
                      <strong>Curator</strong>{" "}
                      {constants.pointsRequiredLevel3.toLocaleString()}
                      <ScreenReaderText>points</ScreenReaderText>
                    </Text>
                  </ProgressMilestones>
                </Progress>
              </ProfileProgress>

              <PointsMobile>
                <PointValue>{pointsLifetime}</PointValue>
                <PointLabel>Lifetime Earnings</PointLabel>

                <PointValue>{pointsBalance}</PointValue>
                <PointLabel>Current Balance</PointLabel>

                <PointValue>{pointsStaked}</PointValue>
                <PointLabel>Staked Balance</PointLabel>
              </PointsMobile>
            </>
          )}
        </ProfileMain>
      </ProfileColumns>

      <ProfileColumnsReverseSmall>
        <ProfileSide>
          {isAdmin && (
            <SideSection>
              <SideHeading size="xs">Admin</SideHeading>
              <Text size="xxs">
                <strong>Hug Token Ids</strong>
                <br />
                <LinkedTokenIds tokenIds={user.hugTokenIds} />
              </Text>

              <Text size="xxs">
                <strong>Platform Roles</strong>
                <br />
                {safeStringArray(user.roles)}
              </Text>

              <Text size="xxs">
                <strong>Platform Id</strong>
                <br />
                {user.id}
              </Text>

              <Text size="xxs">
                <Link to="#rewards-ledger">View Rewards Ledger</Link>
              </Text>

              <Text size="xxs">
                <Link to="#role-overrides">Role Overrides</Link>
              </Text>
            </SideSection>
          )}

          {profilePoints && (
            <PointsSideSection>
              <SideHeading>Points</SideHeading>
              <PointsSidebar>
                <PointValue>{pointsLifetime}</PointValue>
                <PointLabel>Lifetime Earnings</PointLabel>

                <PointValue>{pointsBalance}</PointValue>
                <PointLabel>Current Balance</PointLabel>

                <PointValue>{pointsStaked}</PointValue>
                <PointLabel>Staked Balance</PointLabel>
              </PointsSidebar>
            </PointsSideSection>
          )}
          <SideSection>
            <SideHeading>Wallets</SideHeading>
            <WalletList wallets={user.wallets} />
            {/* <Text size="xxs">
              <Link to="#add-wallet">Add wallet</Link>
            </Text> */}
          </SideSection>

          <SideSection>
            <SideHeading>Discord</SideHeading>
            {isAdmin ? (
              <ProfileDiscord user={user} isAdmin={isAdmin} />
            ) : (
              <DiscordConnect />
            )}
          </SideSection>

          <SideSection>
            <SideHeading>Hugs</SideHeading>
            <Text>
              {isAdmin ? (
                <Button onClick={() => adminRefreshUser(user.id)} size="xs">
                  Refresh User HUGs
                </Button>
              ) : (
                <Button onClick={() => refreshAuth()} size="xs">
                  Refresh My HUGs
                </Button>
              )}
            </Text>
          </SideSection>

          {!!user?.guestPasses?.length && (
            <SideSection>
              <SideHeading>Guest Passes</SideHeading>
              {user.guestPasses.map((p) => p.toLowerCase()).join(",")}
            </SideSection>
          )}
        </ProfileSide>

        <ProfileMain>
          <ProfileReviews id={user.id} />

          {featureFlags.enableRewardsWallet && <ProfileRewards user={user} />}
        </ProfileMain>
      </ProfileColumnsReverseSmall>

      {showRewards && <ProfileAdminRewards rewards={rewards} />}

      {isAdmin && (
        <SColumns cols={2} css={{ gap: "3rem" }}>
          {isAdmin && (
            <AdminOverrides>
              <SH2 size="sm" id="role-overrides">
                Role Overrides
              </SH2>
              <RoleList>
                {(isSuperAdmin ? SUPER_ROLES : ROLES).map((role: string) => (
                  <RoleOverrideButton
                    key={role}
                    role={role}
                    user={user}
                    adminUserAddDiscordOverride={adminUserAddDiscordOverride}
                    adminUserRemoveDiscordOverride={
                      adminUserRemoveDiscordOverride
                    }
                    disabled={isLoading}
                  />
                ))}
              </RoleList>
            </AdminOverrides>
          )}
          {isSuperAdmin && (
            <AdminAccessButtons>
              <SH2
                size="sm"
                id="role-overrides"
                css={{ marginBottom: "1.5rem" }}
              >
                Access
              </SH2>
              {hasAdminRole ? (
                <Text>
                  <ProfileButton
                    onClick={removeAdminRole}
                    disabled={isLoading}
                    arrow={null}
                  >
                    Remove Admin Access
                  </ProfileButton>
                </Text>
              ) : (
                <Text>
                  <ProfileButton
                    onClick={addAdminRole}
                    disabled={isLoading}
                    arrow={null}
                  >
                    Grant Admin Access
                  </ProfileButton>
                </Text>
              )}

              {hasSuperAdminRole ? (
                <Text>
                  <ProfileButton
                    onClick={removeSuperAdminRole}
                    disabled={isLoading}
                    arrow={null}
                  >
                    Remove Super Admin Access
                  </ProfileButton>
                </Text>
              ) : (
                <Text>
                  <ProfileButton
                    onClick={addSuperAdminRole}
                    disabled={isLoading}
                    arrow={null}
                  >
                    Grant Super Admin Access
                  </ProfileButton>
                </Text>
              )}

              {isQA ? (
                <Text>
                  <ProfileButton
                    onClick={removeQA}
                    disabled={isLoading}
                    arrow={null}
                  >
                    Remove Quality Assurance
                  </ProfileButton>
                </Text>
              ) : (
                <Text>
                  <ProfileButton
                    onClick={addQA}
                    disabled={isLoading}
                    arrow={null}
                  >
                    Grant Quality Assurance
                  </ProfileButton>
                </Text>
              )}

              {isCuratorLevel2 ? (
                <Text>
                  <ProfileButton
                    onClick={removeCuratorLevel2}
                    disabled={isLoading}
                    arrow={null}
                  >
                    Remove Jr. Curator (Curator Level 2)
                  </ProfileButton>
                </Text>
              ) : (
                <Text>
                  <ProfileButton
                    onClick={addCuratorLevel2}
                    disabled={isLoading}
                    arrow={null}
                  >
                    Grant Jr. Curator (Curator Level 2)
                  </ProfileButton>
                </Text>
              )}

              {user.discordId && (
                <Text>
                  <ProfileButton
                    onClick={resetDiscord}
                    disabled={isLoading}
                    arrow={null}
                  >
                    Reset Discord Connection
                  </ProfileButton>
                </Text>
              )}
            </AdminAccessButtons>
          )}
        </SColumns>
      )}
    </main>
  );
}

const bpMobile = `@media (max-width: 830px)`;
const bpDesktop = `@media (min-width: 831px)`;

const ProfileColumns = styled.div(({ theme }) => ({
  // paddingTop: "3rem",
  [bpDesktop]: {
    display: "grid",
    gap: "4rem",
    gridTemplateColumns: "clamp(185px, 18vw, 250px) 1fr",
    gridTemplateAreas: "'side main'",
  },
  "&+*": {
    paddingTop: 0,
  },
}));
const ProfileSide = styled.div(({ theme }) => ({
  marginBottom: theme.spacing.lg,
  [bpDesktop]: {
    gridArea: "side",
    marginBottom: theme.spacing.xxl,
  },
}));

const ProfileMain = styled.div(({ theme }) => ({
  marginBottom: theme.spacing.xl,
  [bpDesktop]: {
    gridArea: "main",
    marginBottom: theme.spacing.xxl,
  },
}));

const ProfileColumnsReverseSmall = styled(ProfileColumns)({
  paddingTop: 0,
  [bpMobile]: {
    display: "flex",
    flexDirection: "column-reverse",
  },
});

const SideSection = styled.div(({ theme }) => ({
  fontSize: theme.fontSizes.xs,
  lineHeight: "1.2",
  paddingBottom: "1.66rem",
  p: {
    marginBottom: theme.spacing.sm,
    "&>div:last-of-type:not('.button'), &:last-child:not('.button')": {
      paddingBottom: 0,
      marginBottom: 0,
    },
  },
  "&:last-of-type:not('.button'), &>*:last-child:not('.button')": {
    paddingBottom: 0,
    marginBottom: 0,
  },
}));

const PointsSideSection = styled(SideSection)({
  [bpMobile]: {
    display: "none",
  },
});
const SideHeading = styled(H2)(({ theme }) => ({
  ...theme.fonts.body,
  fontSize: theme.fontSizes.xxs,
  fontWeight: "bold",
  borderBottom: `1px solid ${theme.colors.gray20}`,
  marginBottom: theme.spacing.sm,
  marginTop: "0 !important",
  paddingBottom: theme.spacing.xs,
}));
const SH2 = styled(H2)(({ theme }) => ({
  ...theme.fonts.body,
  borderBottom: "1px solid rgba(0,0,0,.2)",
  fontWeight: "bold",
  marginBottom: 0,
  paddingBottom: ".5rem",
}));
const ProfileHeader = styled.div(({ theme }) => ({
  "&>div:last-of-type": {
    flex: "1 0 0%",
  },
  p: {
    marginBottom: 0,
  },
  [bpMobile]: {
    alignItems: "center",
    display: "flex",
    gap: "1rem",
    justifyContent: "flex-start",
  },
}));

const AvatarWrapper = styled.div(({ theme }) => ({
  alignSelf: "flex-start",
  flex: "0 0 calc(30px + 12vw)",
  maxWidth: "120px",
  position: "relative",
  img: {
    display: "block",
  },
  [bpDesktop]: {
    marginBottom: theme.spacing.md,
  },
}));

const SAvatar = styled(Avatar)(({ theme }) => ({
  borderRadius: theme.borderRadius.round,
  height: "auto",
  maxWidth: "120px",
  width: "calc(30px + 12vw)",
}));

const Name = styled(H1)({
  margin: "0 0 5px 0",
  lineHeight: "1.1",
});

const EditProfile = styled(Link)(({ theme }) => ({
  alignItems: "center",
  color: theme.colors.fg,
  display: "inline-flex",
  fontSize: theme.fontSizes.xxs,
  gap: 5,
  lineHeight: 1.3,
  marginTop: "1em",
  textDecoration: "none",
  // borderBottom: "1px solid currentColor",
  svg: {
    width: 11,
    height: "auto",
  },
}));

const ProfileProgress = styled(Callout)(({ theme }) => ({
  backgroundColor: theme.colors.gray10,
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  lineHeight: 1.3,
  paddingLeft: "1rem",
  paddingRight: "1rem",
  textAlign: "center",
  strong: {
    whiteSpace: "nowrap",
  },
  [bpMobile]: {
    paddingTop: "2rem",
    paddingBottom: "2rem",
  },
  [bpDesktop]: {
    padding: "2rem",
  },
}));

const Points = styled.dl(({ theme }) => ({
  columns: 3,
  display: "block",
  maxWidth: 440,
}));

const PointsSidebar = styled(Points)(({ theme }) => ({
  [bpDesktop]: {
    alignItems: "center",
    columns: "none",
    display: "grid",
    gap: `10px ${theme.spacing.sm}`,
    gridTemplateColumns: "auto 1fr",
    paddingTop: 4,
  },
}));

const PointsMobile = styled(Points)({
  borderBottom: "1px solid rgba(0,0,0,.2)",
  flex: "0 0 100%",
  margin: "0 auto",
  padding: "1.5rem 0",
  textAlign: "center",
  dd: {
    margin: "0 auto",
  },
  [bpDesktop]: {
    display: "none",
  },
});

const PointLabel = styled.dd(({ theme }) => ({
  lineHeight: "1.2",
  fontWeight: "normal",
  fontSize: theme.fontSizes.xxxs,
  marginBottom: 0,
  width: "min-content",
  flex: "1",
  "@media (min-width: 440px)": {
    width: "auto",
  },
}));

const PointValue = styled.dt(({ theme }) => ({
  marginBottom: 3,
  fontSize: theme.fontSizes.xs,
  fontWeight: "bold",
  [bpDesktop]: {
    flex: "0 0 4ch",
    marginBottom: 0,
  },
}));

const Progress = styled.div(({ theme }) => ({
  marginTop: "1.75rem",
}));

const ProgressMilestones = styled.ul(({ theme }) => ({
  display: "flex",
  justifyContent: "space-around",
  padding: "0 0 40px 0",
  position: "relative",
  margin: 0,
  li: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    lineHeight: "1.2",
    padding: 5,
    position: "absolute",
    transform: "translate(-50%, 0)",
  },
  "li::before": {
    backgroundColor: theme.colors.fg,
    bottom: "calc(100% + 4px)",
    content: "''",
    height: "12px",
    opacity: ".4",
    position: "absolute",
    width: "2px",
  },
}));

const ProfileButton = styled(Button)(({ theme }) => ({
  fontSize: theme.fontSizes.xs,
  minHeight: 0,
  padding: ".4em 1em",
}));

const RoleList = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  marginTop: "1.5rem",
}));

const AdminOverrides = styled.div({});

const AdminAccessButtons = styled.div({});

const SColumns = styled(Columns)({
  gap: "3rem",
  "&>*": {
    marginBottom: "3rem",
  },
  [bpMobile]: {
    display: "block",
  },
});
const FinishProfile = styled.div({
  margin: "1em 0 0",
});
const FinishProfileList = styled.ul({
  listStyleType: "none",
  margin: ".5em 0 0",
  padding: 0,
  li: {
    display: "flex",
    alignItems: "center",
    margin: ".3em 0",
  },
});
const DisplayCheckbox = styled.span<{ checked: boolean }>(
  ({ checked, theme }) => ({
    display: "inline-flex",
    borderRadius: theme.borderRadius.xs,
    backgroundColor: theme.colors.gray10,
    width: "1em",
    height: "1em",
    marginRight: 10,
    ...(!checked && {
      backgroundColor: theme.colors.gray10,
      opacity: 0.5,
      mixBlendMode: "multiply",
    }),
    "&::after": {
      content: "''",
      backgroundImage: `url('${iconCheckRounded}')`,
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundSize: "contain",
      display: "inline-block",
      height: "100%",
      width: "100%",
      transform: "translateX(.2em) scale(1.15)",
      filter:
        "brightness(0) saturate(100%) invert(46%) sepia(28%) saturate(7415%) hue-rotate(353deg) brightness(101%) contrast(95%)",
      ...(!checked && {
        color: theme.colors.black,
        filter: "none",
        opacity: 0.5,
      }),
    },
  })
);

export { Profile };
